import { useMutation } from "convex/react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCommonTimeZones } from "@/hooks/useCommonTimeZones";
import { api } from "@convex/_generated/api";
import type { Doc } from "@convex/_generated/dataModel";

const timeOptions = Array.from({ length: 24 }, (_, i) => {
  const hour = i;
  const period = hour < 12 ? "AM" : "PM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return {
    label: `${displayHour}:00 ${period}`,
    value: hour,
  };
});

interface TimeSelectorProps {
  currentSavedSubscription: Doc<"notificationSubscriptions">;
}

export const TimeSelector = ({ currentSavedSubscription }: TimeSelectorProps) => {
  const { preferredNotificationTimeZone, preferredNotificationHour } = currentSavedSubscription;
  const timeZones = useCommonTimeZones(preferredNotificationTimeZone);

  const updateNotificationTime = useMutation(
    api.notificationSubscriptions.updateTime
  ).withOptimisticUpdate(
    (localStore, { preferredNotificationTimeZone, preferredNotificationHour }) => {
      const { pushSubscription } = currentSavedSubscription;
      const current = localStore.getQuery(api.notificationSubscriptions.get, {
        pushSubscription,
      });
      if (current) {
        const updates = {
          ...(preferredNotificationTimeZone !== undefined && { preferredNotificationTimeZone }),
          ...(preferredNotificationHour !== undefined && { preferredNotificationHour }),
        };
        localStore.setQuery(
          api.notificationSubscriptions.get,
          { pushSubscription },
          { ...currentSavedSubscription, ...updates }
        );
      }
    }
  );

  const handleTimeZoneChange = (newTimeZone: string) => {
    void updateNotificationTime({
      subscriptionId: currentSavedSubscription._id,
      preferredNotificationTimeZone: newTimeZone,
    });
  };

  const handleTimeChange = (newTime: string) => {
    void updateNotificationTime({
      subscriptionId: currentSavedSubscription._id,
      preferredNotificationHour: Number(newTime),
    });
  };

  return (
    <>
      <p className="text-sm text-gray-600 mb-3">
        If you have cards to review, you'll receive a daily reminder at the time below.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-500">Time</label>
          <Select value={preferredNotificationHour.toString()} onValueChange={handleTimeChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select time" />
            </SelectTrigger>
            <SelectContent>
              {timeOptions.map((time) => (
                <SelectItem key={time.value} value={time.value.toString()}>
                  {time.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-500">Timezone</label>
          <Select value={preferredNotificationTimeZone} onValueChange={handleTimeZoneChange}>
            <SelectTrigger className="w-full min-w-[200px]">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {timeZones.map((timeZone) => (
                <SelectItem key={timeZone.name} value={timeZone.name}>
                  {timeZone.currentTimeFormat.split(" - ")[0]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
};
